
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ArrowLeft, Package, Edit, Settings, Download, RefreshCw, Filter } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { usePageFocus } from '@/hooks/usePageFocus';

const PRODUCT_CATEGORY: CategoryKey = 'product';
import { companyService } from '@/services/companyService';
import { triggerReloadSearch, pickReloadSearch } from '@/utils/reloadSearch';
import { decodeHtmlEntities } from '@/lib/utils';

interface Product {
  id: number;
  created_at: string;
  product_id: string;
  sku_id: string;
  product_type_code: string;
  category_code: string;
  brand: string;
  sku_status: string;
  warehouse: string;
  sku_name: string;
  sku_name_chi: string;
  sku_name_sc: string;
  sku_short_description: string;
  sku_short_description_chi: string;
  sku_short_description_sc: string;
  sku_long_description: string;
  sku_long_description_chi: string;
  sku_long_description_sc: string;
  manufactured_country: string;
  original_price: number;
  selling_price: number;
  packing_spec_eng: string;
  packing_spec_chi: string;
  packing_spec_sc: string;
  packing_height: number;
  packing_length: number;
  packing_depth: number;
  packing_dimension_unit: string;
  weight: number;
  weight_unit: string;
  packing_box_type: string;
  invisible_flag: string;
  barcode: string;
  fine_print_eng: string;
  fine_print_chi: string;
  fine_print_sc: string;
  oversea_delivery: string;
}

const ProductManagement = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [currentCompanyId, setCurrentCompanyId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [skuId, setSkuId] = useState('');
  const [ean, setEan] = useState('');
  const [skuName, setSkuName] = useState('');
  const [productId, setProductId] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [warehouseOptions, setWarehouseOptions] = useState<string[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [onlineStatusFilter, setOnlineStatusFilter] = useState<'all' | 'ONLINE' | 'OFFLINE'>('all');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // 跟踪加载开始时间，用于检测stale loading状态
  const loadingStartedAtRef = useRef<number | null>(null);

  // 跟踪stale loading恢复定时器
  const staleRecoveryTimerRef = useRef<number | null>(null);
  
  // 首次載入時回放搜尋（恢复UI并自动执行搜索）
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    (async () => {
      console.log('🔄 产品管理：检测到待回放的搜索，开始执行');
      try {
        // 确保 Auth/Session ready 再搜
        await supabase.auth.getSession();
        
        // 回填 UI
        const filters = pending.filters;
        if (filters.searchTerm !== undefined) setSearchTerm(filters.searchTerm);
        if (filters.skuId !== undefined) setSkuId(filters.skuId);
        if (filters.ean !== undefined) setEan(filters.ean);
        if (filters.skuName !== undefined) setSkuName(filters.skuName);
        if (filters.productId !== undefined) setProductId(filters.productId);
        if (filters.warehouseFilter !== undefined) setWarehouseFilter(filters.warehouseFilter);
        if (filters.brandFilter !== undefined) setBrandFilter(filters.brandFilter);
        if (filters.onlineStatusFilter !== undefined) setOnlineStatusFilter(filters.onlineStatusFilter);
        if (filters.page !== undefined) setPage(filters.page);
        if (filters.pageSize !== undefined) setPageSize(filters.pageSize);
        
        // 先加载公司信息和筛选选项
        const c = await companyService.getCurrentCompany();
        setCurrentCompanyId(c ? Number(c.id) : null);
        if (c?.id) {
          await loadFilterOptions(Number(c.id));
        }
        
        // 延迟一点时间让UI状态更新，然后执行搜索
        setTimeout(async () => {
          // 直接传递搜索参数，不依赖state
          await fetchProducts(c ? Number(c.id) : null, filters);
        }, 500);
        
      } catch (error) {
        console.error('产品管理回放搜索失败:', error);
        toast.error(language === 'zh' ? '搜索恢复失败，请重新搜索' : 'Search restore failed, please search again');
      }
    })();
  }, []); // 只在组件挂载时执行一次

  // 页面焦点恢复处理器
  const handlePageFocusRecovery = useCallback(async () => {
    const now = Date.now();
    const STALE_LOADING_THRESHOLD = 3000; // 3秒

    // 检查是否是stale loading状态
    if (loading && loadingStartedAtRef.current) {
      const loadingDuration = now - loadingStartedAtRef.current;
      if (loadingDuration > STALE_LOADING_THRESHOLD) {
        console.log(`🔄 檢測到stale loading狀態 (${loadingDuration}ms)，重置loading狀態`);
        setLoading(false);
        loadingStartedAtRef.current = null;
      } else {
        // 仍在阈值内，安排定时器在剩余时间内触发恢复
        const remainingTime = STALE_LOADING_THRESHOLD - loadingDuration;
        console.log(`🔄 產品管理頁面正在加載中 (${loadingDuration}ms)，安排${remainingTime}ms後自動恢復`);

        // 清除任何现有的定时器
        if (staleRecoveryTimerRef.current) {
          clearTimeout(staleRecoveryTimerRef.current);
        }

        // 安排自动恢复
        staleRecoveryTimerRef.current = window.setTimeout(async () => {
          console.log('🔄 自動恢復：重置loading狀態並重新加載數據');
          setLoading(false);
          loadingStartedAtRef.current = null;
          staleRecoveryTimerRef.current = null;

          try {
            await fetchProducts(currentCompanyId);
          } catch (error) {
            console.error('產品管理頁面自動恢復失敗:', error);
          }
        }, remainingTime);

        return;
      }
    } else if (loading) {
      console.log('🔄 產品管理頁面正在加載中，跳過焦點恢復');
      return;
    }

    try {
      console.log('🔄 產品管理頁面焦點恢復，重新加載數據');
      await fetchProducts(currentCompanyId);
    } catch (error) {
      console.error('產品管理頁面焦點恢復失敗:', error);
    }
  }, [loading, currentCompanyId]);

  // 使用页面焦点恢复 hook
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000,
    enabled: false
  });

  // 組件卸載時清理狀態
  useEffect(() => {
    return () => {
      // 清理搜索相關狀態
      setSearchTerm('');
      setSkuId('');
      setEan('');
      setSkuName('');
      setProductId('');
      setWarehouseFilter('');
      setBrandFilter('');
      setOnlineStatusFilter('');
      setPage(1);
      setPageSize(50);
      setProducts([]);
      setTotal(0);
      setLoading(false);
    };
  }, []);
  



  const loadFilterOptions = async (companyIdNum: number) => {
    try {
      const { data: whData } = await (supabase
        .from('products')
        .select('warehouse') as any)
        .eq('company_id', companyIdNum)
        .not('warehouse', 'is', null)
        .order('warehouse');
      const { data: brData } = await (supabase
        .from('products')
        .select('brand') as any)
        .eq('company_id', companyIdNum)
        .not('brand', 'is', null)
        .order('brand');
      setWarehouseOptions(Array.from(new Set((whData as any[] || []).map((r: any) => String(r.warehouse)))).filter(Boolean) as string[]);
      setBrandOptions(Array.from(new Set((brData as any[] || []).map((r: any) => String(r.brand)))).filter(Boolean) as string[]);
    } catch (e) {
      // non-blocking
    }
  };

  const fetchProducts = async (companyId?: number | null, searchParams?: any) => {
    setLoading(true);
    loadingStartedAtRef.current = Date.now(); // 记录加载开始时间
    setErrorMessage(null);
    try {
      // 确保 Auth 就緒
      await ensureAuthReady();

      // Build server-side filters - 使用传入的参数或当前state
      const params = searchParams || {
        searchTerm, skuId, ean, skuName, productId, 
        warehouseFilter, brandFilter, onlineStatusFilter, page, pageSize
      };
      
      const from = (params.page - 1) * params.pageSize;
      const to = from + params.pageSize - 1;
      
      const toNullable = (v?: string) => (v?.trim() ? v.trim() : undefined);
      const q = toNullable(params.searchTerm);

      let query = supabase
        .from('products')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      // Company scoping: only show current company's products
      let companyIdNum = companyId ?? currentCompanyId ?? Number(localStorage.getItem('selectedCompanyId') || '0');
      if (!companyIdNum || !Number.isFinite(companyIdNum as number)) {
        // If company not available yet (e.g., after long idle), skip querying to prevent spinner hang
        throw new Error(language === 'zh' ? '尚未選擇公司，請先切換公司' : 'No company selected. Please choose a company.');
      }
      if (companyIdNum && Number.isFinite(companyIdNum as number)) {
        query = (query as any).eq('company_id', companyIdNum as number);
      }

      // Apply OR first (combined), then AND filters
      if (q) {
        query = query.or(
          `sku_id.ilike.%${q}%,sku_name.ilike.%${q}%,brand.ilike.%${q}%,product_id.ilike.%${q}%,barcode.ilike.%${q}%`
        );
      }
      if (toNullable(params.skuId)) query = query.ilike('sku_id', `%${toNullable(params.skuId)}%`);
      if (toNullable(params.ean)) query = query.ilike('barcode', `%${toNullable(params.ean)}%`);
      if (toNullable(params.skuName)) query = query.or(
        `sku_name.ilike.%${toNullable(params.skuName)}%,sku_name_chi.ilike.%${toNullable(params.skuName)}%,sku_name_sc.ilike.%${toNullable(params.skuName)}%`
      );
      if (toNullable(params.productId)) {
        const d = toNullable(params.productId);
        query = query.or(
          `sku_short_description.ilike.%${d}%,sku_short_description_chi.ilike.%${d}%,sku_short_description_sc.ilike.%${d}%,sku_long_description.ilike.%${d}%,sku_long_description_chi.ilike.%${d}%,sku_long_description_sc.ilike.%${d}%`
        );
      }
      if (toNullable(params.warehouseFilter)) query = query.eq('warehouse', toNullable(params.warehouseFilter));
      if (toNullable(params.brandFilter)) query = query.eq('brand', toNullable(params.brandFilter));
      if (params.onlineStatusFilter !== 'all') {
        query = query.eq('sku_status', params.onlineStatusFilter);
      }

      // Execute simplified query
      const { data, error, count } = await query;
      if (error) throw error;

      console.log('產品數據載入成功:', data?.length || 0, '項產品，總計:', count || 0);
      setProducts(data || []);
      setTotal(count || 0);
    } catch (error: any) {
      console.error('获取产品数据失败:', error);
      const msg = String(error?.message || error);
      
      if (msg.includes('尚未選擇公司') || msg.includes('No company selected')) {
        // 公司未選擇的特殊情況
        setProducts([]);
        setTotal(0);
        setErrorMessage(msg);
        return;
      } else {
        setErrorMessage((language === 'zh' ? '獲取產品資料失敗: ' : 'Failed to fetch products: ') + msg);
      }
      toast.error(language === 'zh' ? '載入失敗' : 'Load failed');
    } finally {
      setLoading(false);
      loadingStartedAtRef.current = null; // 清除加载开始时间

      // 清除stale recovery定时器
      if (staleRecoveryTimerRef.current) {
        clearTimeout(staleRecoveryTimerRef.current);
        staleRecoveryTimerRef.current = null;
      }
    }
  };

  const handleEditProduct = (productId: number) => {
    navigate(`/product-overview/management/edit/${productId}`);
  };

  const handleSearch = () => {
    // 收集当前的搜索条件
    const filters = {
      searchTerm,
      skuId,
      ean,
      skuName,
      productId,
      warehouseFilter,
      brandFilter,
      onlineStatusFilter,
      page: 1, // 搜索时重置为第一页
      pageSize
    };
    
    console.log('🔄 产品管理：触发专业刷新搜索，条件:', filters);
    triggerReloadSearch(filters);
  };

  const handleReset = () => {
    // 重置所有搜索条件
    const resetFilters = {
      searchTerm: '',
      skuId: '',
      ean: '',
      skuName: '',
      productId: '',
      warehouseFilter: '',
      brandFilter: '',
      onlineStatusFilter: 'all' as const,
      page: 1,
      pageSize
    };
    
    console.log('🔄 产品管理：重置搜索条件并刷新');
    triggerReloadSearch(resetFilters);
  };


  const getStatusBadge = (status: string) => {
    if (status === 'ACTIVE' || status === 'active') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (status === 'INACTIVE' || status === 'inactive') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusText = (status: string) => {
    const normalized = (status || '').toUpperCase();
    const isOnline = normalized === 'ONLINE' || normalized === 'ACTIVE';
    const isOffline = normalized === 'OFFLINE' || normalized === 'INACTIVE';
    if (language === 'zh') {
      if (isOnline) return '在線';
      if (isOffline) return '離線';
    } else {
      if (isOnline) return 'Online';
      if (isOffline) return 'Offline';
    }
    return status;
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '產品管理' : 'Product Management'}</h1>
          </div>
        </div>

        {/* Search Section */}
        <Card className="bg-card/80 border-border mb-6">
          <CardContent className="p-6">
            {/* Search Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'zh' ? '產品 ID' : 'SKU ID'}</label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品 ID' : 'Search SKU ID'}
                  value={skuId}
                  onChange={(e) => setSkuId(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'zh' ? '條碼' : 'Barcode'}</label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 條碼' : 'Search Barcode'}
                  value={ean}
                  onChange={(e) => setEan(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'zh' ? '產品名稱' : 'Product Name'}</label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品名稱' : 'Search Product Name'}
                  value={skuName}
                  onChange={(e) => setSkuName(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{language === 'zh' ? '產品內容' : 'Product Description'}</label>
                <Input
                  placeholder={language === 'zh' ? '搜尋 產品內容' : 'Search Product Description'}
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  className="bg-background/50 border-input text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Warehouse dropdown with options */}
              <Select value={warehouseFilter} onValueChange={(v) => { setWarehouseFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="h-10 bg-background/50 border-input text-foreground hover:bg-secondary/50">
                  <SelectValue placeholder={language === 'zh' ? '全部倉庫' : 'All Warehouse'} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  <SelectItem value="all">{language === 'zh' ? '全部倉庫' : 'All Warehouse'}</SelectItem>
                  {warehouseOptions.map((w) => (
                    <SelectItem key={w} value={w} className="focus:bg-accent data-[highlighted]:bg-accent">{w}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Online status dropdown */}
              <Select value={onlineStatusFilter} onValueChange={(v) => setOnlineStatusFilter(v as any)}>
                <SelectTrigger className="h-10 bg-background/50 border-input text-foreground hover:bg-secondary/50">
                  <SelectValue placeholder={language === 'zh' ? '在線狀態' : 'Online Status'} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  <SelectItem value="all">{language === 'zh' ? '所有狀態' : 'All Status'}</SelectItem>
                  <SelectItem value="ONLINE">{language === 'zh' ? '在線' : 'Online'}</SelectItem>
                  <SelectItem value="OFFLINE">{language === 'zh' ? '離線' : 'Offline'}</SelectItem>
                </SelectContent>
              </Select>
              {/* Brand dropdown with options */}
              <Select value={brandFilter} onValueChange={(v) => { setBrandFilter(v === 'all' ? '' : v); }}>
                <SelectTrigger className="h-10 bg-background/50 border-input text-foreground hover:bg-secondary/50">
                  <SelectValue placeholder={language === 'zh' ? '品牌名稱' : 'Brand Name'} />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border text-popover-foreground">
                  <SelectItem value="all">{language === 'zh' ? '全部品牌' : 'All Brands'}</SelectItem>
                  {brandOptions.map((b) => (
                    <SelectItem key={b} value={b} className="focus:bg-accent data-[highlighted]:bg-accent">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '重設' : 'Reset'}
                </Button>
                <Button
                  onClick={handleSearch}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '搜尋' : 'Search'}
                </Button>
              </div>
              <div />
            </div>
          </CardContent>
        </Card>

        {/* Results Summary + Pagination Controls */}
        <div className="mb-4 flex items-center justify-between text-sm text-muted-foreground">
          {(() => {
            const from = (page - 1) * pageSize + 1;
            const to = Math.min(page * pageSize, total);
            return (
              <span>
                顯示 {total === 0 ? 0 : from}-{to} / {total} 筆
              </span>
            );
          })()}

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">{language === 'zh' ? '每頁' : 'Per page'}</span>
              <Select value={String(pageSize)} onValueChange={async (v) => { if (loading) return; setPageSize(Number(v)); setPage(1); await fetchProducts(); }}>
                <SelectTrigger className="h-8 w-24 bg-card/50 border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-8 bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {language === 'zh' ? '上一頁' : 'Prev'}
              </Button>
              <span className="text-muted-foreground">{language === 'zh' ? '第 ' : 'Page '}{page}{language === 'zh' ? ' 頁' : ''}</span>
              <Button
                variant="outline"
                className="h-8 bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
                disabled={page * pageSize >= total || loading}
                onClick={async () => { if (loading) return; setPage((p) => p + 1); await fetchProducts(); }}
              >
                {language === 'zh' ? '下一頁' : 'Next'}
              </Button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <Card className="bg-card/80 border-border shadow-lg shadow-black/30">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
                <p className="text-muted-foreground ml-3">{language === 'zh' ? '載入中...' : 'Loading...'}</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{errorMessage || (language === 'zh' ? '沒有找到產品' : 'No products found')}</p>
                {errorMessage && (
                  <div className="mt-4">
                    <Button onClick={() => fetchProducts()} className="bg-purple-600 hover:bg-purple-700 text-white">
                      {language === 'zh' ? '重新嘗試' : 'Retry'}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/60">
                    <TableRow>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品 ID' : 'SKU ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '條碼' : 'Barcode'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '倉庫' : 'Warehouse'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '在線狀態' : 'Status'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '操作' : 'Action'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product, idx) => (
                      <TableRow key={product.id} className={`${idx % 2 === 0 ? 'bg-card/40' : ''} hover:bg-accent/30`}>
                        <TableCell className="text-blue-400 font-medium">
                          {product.sku_id}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {product.barcode || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {product.brand || '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-xs">
                          <div className="line-clamp-2">
                            {decodeHtmlEntities(language === 'zh' ? (product.sku_name_chi || product.sku_name) : (product.sku_name || product.sku_name_chi) || '')}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {product.warehouse || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${ ((product.sku_status || '').toUpperCase() === 'ONLINE') ? 'bg-emerald-400' : 'bg-muted-foreground' }`}></div>
                            <span className="text-muted-foreground">{getStatusText(product.sku_status)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditProduct(product.id)}
                            className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                          >
                            {t('edit')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductManagement;
